import React, { useState, useEffect } from 'react';
import { Menu, ChevronDown, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { canSwitchOrganization } from '../utils/rbac';
import { apiClient } from '../api/client';
import OrganizationSwitcher from './OrganizationSwitcher';

const TopBar = ({ user, dashboardConfig, selectedCustomerId, onCustomerChange, onMenuClick }) => {
  const { logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);

  return (
    <div className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left side */}
          <div className="flex items-center">
            <button
              onClick={onMenuClick}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              <Menu className="w-6 h-6" />
            </button>
            
            <div className="lg:ml-0 ml-4">
              <h1 className="text-2xl font-bold text-apple-blue-black">
                {dashboardConfig.title}
              </h1>
              <p className="text-sm text-apple-aluminum">
                {dashboardConfig.subtitle}
              </p>
            </div>
          </div>

          {/* Center - Organization Switcher */}
          {dashboardConfig.showOrgSwitcher && canSwitchOrganization(user) && (
            <div className="flex-1 max-w-md mx-8">
              <OrganizationSwitcher
                selectedCustomerId={selectedCustomerId}
                onCustomerChange={onCustomerChange}
              />
            </div>
          )}

          {/* Right side */}
          <div className="flex items-center space-x-4">
            {/* Customer name for non-admin users */}
            {!canSwitchOrganization(user) && user.customer_name && (
              <div className="hidden sm:block">
                <span className="text-sm text-gray-500">Organization:</span>
                <span className="ml-1 text-sm font-medium text-gray-900">
                  {user.customer_name}
                </span>
              </div>
            )}

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-3 p-2 rounded-md text-gray-700 hover:bg-gray-100"
              >
                <div className="w-8 h-8 bg-apple-sky-blue rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-cobalt-500">
                    {user.displayName?.[0] || user.email?.[0] || 'U'}
                  </span>
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-apple-blue-black">
                    {user.displayName || user.email?.split('@')[0]}
                  </p>
                  <p className="text-xs text-apple-aluminum capitalize">
                    {user.role?.replace('_', ' ')}
                  </p>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {/* Dropdown menu */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">
                      {user.displayName || user.email?.split('@')[0]}
                    </p>
                    <p className="text-xs text-gray-600">{user.email}</p>
                    {user.metadata?.lastSignInTime && (
                      <p className="text-xs text-gray-500 mt-1">
                        Last login: {new Date(user.metadata.lastSignInTime).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      logout();
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Click outside to close user menu */}
      {showUserMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowUserMenu(false)}
        />
      )}
    </div>
  );
};

export default TopBar;